import { CompositionDefinition } from 'types/composition';
import { SpaceValidationError, SpaceValidationResult } from 'types/space';
import { DEFAULT_SPACE_DOCUMENT_LIMITS } from './limits';
import { walkComponentGraph } from './componentGraph';
import { findForbiddenPropKey } from './forbiddenPropKeys';

const VALID_KINDS = new Set(['surface', 'section', 'fragment']);

/**
 * Validates a composition's stored definition (`kind` + `interface` + `a2ui` subtree). Reuses the
 * same graph-walking, forbidden-key, and size/depth limits as `validateSpaceDocument` — a
 * composition's `a2ui` is the same component-tree shape as a Space document's, just without the
 * envelope fields (`formatVersion`/`surface`/`wires`), which don't apply to a reusable subtree.
 * Fail-closed: any structural ambiguity is an error, never a best-effort pass.
 */
export function validateCompositionDefinition(
  doc: unknown,
  limits = DEFAULT_SPACE_DOCUMENT_LIMITS
): SpaceValidationResult {
  const errors: SpaceValidationError[] = [];

  if (!doc || typeof doc !== 'object') {
    return {
      valid: false,
      errors: [{ code: 'MISSING_FORMAT_VERSION', message: 'Composition definition must be an object' }],
    };
  }

  const candidate = doc as Partial<CompositionDefinition>;

  if (!candidate.kind || !VALID_KINDS.has(candidate.kind)) {
    errors.push({
      code: 'INVALID_KIND',
      message: `Composition "kind" must be one of surface|section|fragment (received "${String(candidate.kind)}")`,
      path: 'kind',
    });
  }

  if (!candidate.interface || typeof candidate.interface !== 'object') {
    errors.push({
      code: 'INVALID_INTERFACE',
      message: 'Composition definition is missing required field "interface"',
      path: 'interface',
    });
  } else {
    const { props, slots, emits } = candidate.interface;
    if (props && typeof props !== 'object') {
      errors.push({ code: 'INVALID_INTERFACE', message: '"interface.props" must be an object', path: 'interface/props' });
    }
    if (slots !== undefined && !Array.isArray(slots)) {
      errors.push({ code: 'INVALID_INTERFACE', message: '"interface.slots" must be an array', path: 'interface/slots' });
    }
    if (emits !== undefined && !Array.isArray(emits)) {
      errors.push({ code: 'INVALID_INTERFACE', message: '"interface.emits" must be an array', path: 'interface/emits' });
    }
  }

  if (!candidate.a2ui || typeof candidate.a2ui !== 'object') {
    errors.push({
      code: 'MISSING_A2UI',
      message: 'Composition definition is missing required field "a2ui"',
      path: 'a2ui',
    });
    return { valid: false, errors };
  }

  const { a2ui } = candidate;

  if (!Array.isArray(a2ui.components)) {
    errors.push({ code: 'MISSING_COMPONENTS', message: 'a2ui.components is required and must be an array', path: 'a2ui/components' });
    return { valid: false, errors };
  }

  if (a2ui.components.length > limits.maxComponents) {
    errors.push({
      code: 'COMPONENT_COUNT_EXCEEDS_LIMIT',
      message: `a2ui.components exceeds maximum of ${limits.maxComponents} entries (received ${a2ui.components.length})`,
      path: 'a2ui/components',
    });
  }

  const payloadSize = Buffer.byteLength(JSON.stringify(a2ui.components), 'utf-8');
  if (payloadSize > limits.maxPayloadBytes) {
    errors.push({
      code: 'PAYLOAD_SIZE_EXCEEDS_LIMIT',
      message: `a2ui.components payload exceeds maximum of ${Math.round(limits.maxPayloadBytes / 1024)}KB (received ${Math.round(payloadSize / 1024)}KB)`,
      path: 'a2ui/components',
    });
  }

  const seenIds = new Set<string>();
  a2ui.components.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || !entry.id) {
      errors.push({ code: 'MISSING_COMPONENTS', message: `a2ui.components[${index}] is missing a valid "id"`, path: `a2ui/components/${index}` });
      return;
    }
    if (seenIds.has(entry.id)) {
      errors.push({ code: 'DUPLICATE_COMPONENT_ID', message: `Duplicate component id "${entry.id}" at a2ui.components[${index}]`, path: `a2ui/components/${index}` });
    }
    seenIds.add(entry.id);

    const forbiddenKeyPath = findForbiddenPropKey(entry);
    if (forbiddenKeyPath) {
      errors.push({
        code: 'FORBIDDEN_PROP_KEY',
        message: `Component "${entry.id}" props contain a forbidden key at "${forbiddenKeyPath}"`,
        path: `a2ui/components/${index}/${forbiddenKeyPath}`,
      });
    }
  });

  if (!a2ui.root) {
    errors.push({ code: 'MISSING_ROOT', message: 'a2ui.root is required and must reference a component id in a2ui.components', path: 'a2ui/root' });
  } else if (!seenIds.has(a2ui.root)) {
    errors.push({ code: 'ROOT_NOT_FOUND', message: `a2ui.root "${a2ui.root}" does not match any entry in a2ui.components`, path: 'a2ui/root' });
  } else {
    const issue = walkComponentGraph(a2ui.components, a2ui.root, limits.maxNestingDepth);
    if (issue?.type === 'CYCLE') {
      errors.push({ code: 'CYCLE_DETECTED', message: `Cyclic component reference detected: ${issue.path.join(' -> ')}`, path: `a2ui/components/${issue.path[0]}` });
    } else if (issue?.type === 'DEPTH_EXCEEDED') {
      errors.push({ code: 'NESTING_DEPTH_EXCEEDS_LIMIT', message: `Component tree exceeds maximum nesting depth of ${limits.maxNestingDepth} (path: ${issue.path.join(' -> ')})`, path: `a2ui/components/${issue.path[0]}` });
    }
  }

  return { valid: errors.length === 0, errors };
}
